/**
 * Solicitudes de Matrícula (DGII / proveedores) — listado y creación.
 *
 * Cada solicitud agrupa N chasis (la Autorización de Pago de DGII cubre el
 * grupo completo); el detalle vive en solicitudmatricula.js, abierto aquí en
 * diálogo a pantalla completa. La alerta principal del listado es la fecha
 * límite de pago de la autorización (DGII da ~10 días; vencida hay que
 * reiniciar el trámite): semáforo rojo vencida, naranja por vencer.
 */
import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import SolicitudMatricula, { chipEstado } from "modules/matriculas/solicitudmatricula";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const FILTROS_ESTADO = [
  { value: "", label: "Todas" },
  { value: "borrador", label: "Borrador" },
  { value: "solicitada", label: "Solicitada" },
  { value: "autorizada", label: "Autorizada" },
  { value: "pagada", label: "Pagada" },
  { value: "cerrada", label: "Cerrada" },
  { value: "cancelada", label: "Cancelada" },
];

const FILTROS_TIPO = [
  { value: "", label: "Todas" },
  { value: "dgii", label: "DGII" },
  { value: "proveedor", label: "Proveedor" },
];

/** Días desde hoy hasta 'dd/mm/aaaa' (negativo = vencida). */
const diasHasta = (ddmmaaaa) => {
  const p = String(ddmmaaaa || "").split("/");
  if (p.length !== 3) return null;
  const fecha = new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return Math.round((fecha - hoy) / 86400000);
};

/** El semáforo solo aplica mientras el pago está pendiente. */
const semaforoPago = (row) => {
  if (!row.fechalimitepago || !["solicitada", "autorizada"].includes(row.estado)) return null;
  const dias = diasHasta(row.fechalimitepago);
  if (dias == null) return null;
  if (dias < 0) return { label: `Vencida (${row.fechalimitepago})`, bg: "#FFEBEE", fg: "#C62828" };
  if (dias <= 3) return { label: `Vence ${row.fechalimitepago}`, bg: "#FFF3E0", fg: "#E65100" };
  return { label: row.fechalimitepago, bg: "#ECEFF1", fg: "#455A64" };
};

export default function Solicitudes() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");

  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [nuevaTipo, setNuevaTipo] = useState("dgii");
  const [nuevaGestor, setNuevaGestor] = useState("");
  const [nuevaProveedor, setNuevaProveedor] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresCargando, setProveedoresCargando] = useState(false);
  const [creando, setCreando] = useState(false);

  const [detalleId, setDetalleId] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/solicitudmatricula_listar", {
        estado: filtroEstado,
        tipo: filtroTipo,
      });
      if (ok && Array.isArray(data.solicitudes)) {
        setRows(data.solicitudes);
      } else {
        showAlert({
          message: data.Error || "No se pudieron cargar las solicitudes",
          color: "error",
        });
      }
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, filtroEstado, filtroTipo]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Catálogo de proveedores registrados (suplidores de Cuentas x Pagar):
  // se carga la primera vez que la vía del diálogo es "proveedor".
  useEffect(() => {
    if (!nuevaOpen || nuevaTipo !== "proveedor" || proveedores.length) return undefined;
    let activo = true;
    setProveedoresCargando(true);
    post("/api/solicitudmatricula_proveedores", {})
      .then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.proveedores)) setProveedores(data.proveedores);
      })
      .finally(() => activo && setProveedoresCargando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuevaOpen, nuevaTipo]);

  const crear = async () => {
    setCreando(true);
    try {
      const { ok, data } = await post("/api/solicitudmatricula_crear", {
        tipo: nuevaTipo,
        gestor: nuevaGestor,
        proveedor_id: nuevaTipo === "proveedor" && nuevaProveedor ? nuevaProveedor.id : null,
        destino: nuevaTipo === "proveedor" && nuevaProveedor ? nuevaProveedor.descrip : "",
      });
      if (ok) {
        setNuevaOpen(false);
        showAlert({ message: "Solicitud creada", color: "success" });
        cargar();
        setDetalleId(data.id);
      } else {
        showAlert({ message: data.Error || "No se pudo crear la solicitud", color: "error" });
      }
    } finally {
      setCreando(false);
    }
  };

  const columnas = [
    { field: "id", headerName: "#", width: 70 },
    {
      field: "tipo",
      headerName: "Vía",
      width: 100,
      renderCell: (p) => (p.value === "dgii" ? "DGII" : "Proveedor"),
    },
    { field: "fecha_crea", headerName: "Creada", width: 100 },
    { field: "servicio", headerName: "Servicio", flex: 1.2, minWidth: 150 },
    { field: "gestor", headerName: "Gestor", flex: 0.9, minWidth: 120 },
    { field: "numerodocumento", headerName: "Autorización", flex: 0.9, minWidth: 120 },
    {
      field: "estado",
      headerName: "Estado",
      width: 115,
      renderCell: (p) => chipEstado(p.value),
    },
    {
      field: "chasis_total",
      headerName: "Chasis",
      width: 110,
      renderCell: (p) => {
        const obs = p.row.chasis_observados;
        return (
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
            {p.row.chasis_recibidos}/{p.row.chasis_total}
            {obs > 0 && (
              <Typography component="span" sx={{ fontSize: "0.75rem", color: "#C62828", ml: 0.6 }}>
                ({obs} obs.)
              </Typography>
            )}
          </Typography>
        );
      },
    },
    {
      field: "fechalimitepago",
      headerName: "Límite de pago",
      width: 150,
      renderCell: (p) => {
        const s = semaforoPago(p.row);
        if (!s) return p.value || "";
        return (
          <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.fg, fontWeight: 700 }} />
        );
      },
    },
    {
      field: "abrir",
      headerName: "",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Button
          size="small"
          onClick={() => setDetalleId(p.row.id)}
          sx={{ ...btnBase, color: FP.color.navy }}
        >
          Abrir
        </Button>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Solicitudes de Matrícula"
        subtitle="Trámites de placas ante la DGII y retiro de documentos de proveedores"
        cta={
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              setNuevaTipo("dgii");
              setNuevaGestor("");
              setNuevaProveedor(null);
              setNuevaOpen(true);
            }}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Nueva solicitud
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              sx={fpInputSx}
            >
              {FILTROS_ESTADO.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              label="Vía"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              sx={fpInputSx}
            >
              {FILTROS_TIPO.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={8} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              startIcon={
                cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
              }
              onClick={cargar}
              disabled={cargando}
              sx={btnOutline(FP.color.navy)}
            >
              Actualizar
            </Button>
          </Grid>
        </Grid>

        <DataGrid
          autoHeight
          rows={rows}
          columns={columnas}
          loading={cargando}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={fpDataGridSx}
        />
      </Box>

      {/* Nueva solicitud */}
      <Dialog open={nuevaOpen} onClose={() => setNuevaOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nueva solicitud de matrículas</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Vía"
              value={nuevaTipo}
              onChange={(e) => setNuevaTipo(e.target.value)}
              sx={fpInputSx}
            >
              <MenuItem value="dgii">DGII (trámite de placas)</MenuItem>
              <MenuItem value="proveedor">Proveedor (retiro de documentos)</MenuItem>
            </TextField>
            {nuevaTipo === "proveedor" && (
              <Autocomplete
                options={proveedores}
                loading={proveedoresCargando}
                value={nuevaProveedor}
                onChange={(_, v) => setNuevaProveedor(v)}
                getOptionLabel={(o) => o.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.descrip}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.rnc && `RNC ${o.rnc}`, o.telefono].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Proveedor"
                    helperText={
                      !proveedoresCargando && proveedores.length === 0
                        ? "Sin proveedores registrados (se administran en Cuentas x Pagar)"
                        : ""
                    }
                    sx={fpInputSx}
                  />
                )}
              />
            )}
            <TextField
              fullWidth
              label="Gestor / responsable (opcional)"
              value={nuevaGestor}
              onChange={(e) => setNuevaGestor(e.target.value)}
              sx={fpInputSx}
            />
            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
              La solicitud se crea en borrador: el resto de los datos y los chasis se agregan en el
              detalle.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevaOpen(false)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={crear}
            disabled={creando}
            startIcon={creando ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle a pantalla completa */}
      <Dialog fullScreen open={detalleId != null} onClose={() => setDetalleId(null)}>
        {detalleId != null && (
          <SolicitudMatricula id={detalleId} onExit={() => setDetalleId(null)} onChanged={cargar} />
        )}
      </Dialog>
    </Box>
  );
}
